import "./polyfills";
import {
    type BlockContainerSlotContextValue,
    type BlockDefinition,
    type BlockInstance,
    type PageRenderer,
    type PageRendererArgs,
    type PageRendererFeatureFlags,
    type PageRendererResult,
    type PageRendererUpdateArgs,
    type CompileVibeCodingBlockProps,
    type CompileVibeCodingBlockResult,
    urlMatchesDestination,
    BlockAPI,
    PageAPI,
} from "@softr-io/blocks-sdk";
import { Root, createRoot } from "react-dom/client";
import { AppRoot, PageRef } from "./AppRoot";
import { AppContext } from "./AppContext";
import { flushSync } from "react-dom";
import { injectCustomCode } from "./custom-code";
import {
    SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED,
    SOFTR_PAGE_RENDERER_LOAD,
    addReactBlockReadyEventListener,
} from "./events";
import { Lifecycle } from "./Lifecycle";
import { isPreCompiledBlock } from "./PreCompiledBlockDefinition";
import { withPatchedLoadListener } from "./withPatchedLoadListener";
import { isHeavyLoggingEnabled, log } from "./logger";
import { createContext, startTransition, StrictMode } from "react";
import { makeBlockStabilizer } from "./blockStabilizer";
import { ChildBlock } from "./ChildBlock";
import { insertHeadStylesSentinel } from "./headStylesSentinel";
import { initTrafficFeatureFlags } from "./trafficFeatureFlags";
import { embeddedMode } from "./utils";
import { AppConstants, AppConstantsContext } from "./AppConstantsContext";
import "./reset-styles.css";
import "./global-styles.css";
import { addNavigationListener, SPALink, spaNavigate, useSPALinkClick } from "./spa";
import * as vibeCompiler from "./vibe-compiler";
import { throwAtCaller, timeout } from "@softr/utils";
import { getBlockParentIdMap } from "./block-root";
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { setModalSearchParams, setOpenPageModal, useModalDepth, useOpenPageModal } from "./openInModal";
import { useModalRecord } from "./modalRecordStore";
import { useRecordsByBlock } from "./recordsByBlockStore";
import { isCustomCodeBlock } from "./BlockDefinitionUtils";
import { useNavigationBlocker } from "./useNavigationBlocker";
import { interceptAndStoreBeforeInstallPromptEvent, shouldRegisterServiceWorker, usePushPrompt } from "./pwa/pwa";
import { BlockAPIReadyEvent } from "./BlockAPIReadyEvent";
import { isSafari } from "react-device-detect";

const environment = process.env.SOFTR_ENVIRONMENT ?? "stage";

// Temporary diagnostic logging for a customer's "Failed to get records" report in the
// calendar block. Sentry is disabled for everyone else (see SE-18697). Remove this
// scoped init once the root cause of the intermittent fetch failure is confirmed.
const SENTRY_DEBUG_APPLICATION_ID = "f93e84e6-d692-4973-81f5-e9db46125ce3";
let sentryInitialized = false;

function initSentryForDebugging(): void {
    console.info(`Initializing sentry with environment ${environment}`);
    Sentry.init({
        dsn: "https://a62737e3b34d0a3f3daf64083d9d82de@o364296.ingest.us.sentry.io/4507341521223680",
        environment,
        sampleRate: 1,
        // Just before appending custom code, we set the sourceURL to softr-custom-code so that we can filter
        // out custom code errors from sentry
        denyUrls: [/softr-custom-code/, /softr\/custom-code/],
        // allowUrls matches the top of the stack trace. If any of these matches the URL of the
        // script, the error is kept. Otherwise, it's filtered out. This cuts down on the noise
        // caused by code that we don't control, e.g. third party scripts loaded by custom code
        allowUrls: [
            /https:\/\/assets\.softr-files\/.io/,
            // Adding localhost here for easy testing
            // By default, we don't initialize sentry when using the redirector,
            // I find it useful to add `|| true` to the condition above to check that we're
            // passing/rejecting correct errors to sentry
            /http:\/\/localhost:5001/,
            /http:\/\/localhost:5002/,
        ],
        ignoreErrors: [
            // Bad code in the platform which tries to access window.top from inside an embedded page,
            // which is not allowed by the browser.
            /Blocked a frame with origin/,
        ],
        beforeSend(event) {
            if (event.tags?.["softr.custom-code"]) {
                console.info("Not sending custom code error to sentry: ", event);
                return null;
            }

            return event;
        },
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: ["softr"],
                behaviour: "drop-error-if-contains-third-party-frames",
            }),
        ],
        tracesSampleRate: 0.0005,
    });
}

// Blocks expect these globals because they are bundled separately
// In the blocks side rspack config, these are defined as externals
// Compiled block entrypoints will look like this
// const { useState, useEffect, createPortal, ...etc } = window.__softr_React;
// function BlockName(props) {
//    useState(...);
// }
window.__softr_React = React;
window.__softr_ReactDOM = ReactDOM;

declare global {
    interface Window {
        // These names should match the ones defined in `rspack.config.ts`
        __softr_React: typeof React;
        __softr_ReactDOM: typeof ReactDOM;
    }
}

declare global {
    interface Window {
        isNavigationTransitionsEnabled?: boolean;
    }
}
const NAV_TRANSITION_DURATION = "1.5s";
const removeViewTransitions = installViewTransitions();

if (shouldEnableSPA()) {
    localStorage.setItem("SPA_MODE", "true");
}
if (shouldDisableSPA()) {
    localStorage.removeItem("SPA_MODE");
}

const SPA_MODE_DEFAULT_VAULE = localStorage.getItem("SPA_MODE") === "true";

async function render({
    blocks,
    appId,
    pageId,
    workspaceId,
    paymentPlan,
    targetElement: contentRoot,
    baseUrl,
    theme,
    studioEventEmitter,
    appCustomHeaderCode,
    pageCustomHeaderCode,
    appCustomFooterCode,
    pageCustomFooterCode,
    mode,
    blockIdAttributeName,
    slotIdAttributeName,
    slotBlockIdAttributeName,
    featureFlags: providedFeatureFlags,
    context,
    aiApp,
    badgeType,
    showMadeWithBadge,
    studioUser,
}: PageRendererArgs): Promise<PageRendererResult> {
    if (appId === SENTRY_DEBUG_APPLICATION_ID && !sentryInitialized) {
        sentryInitialized = true;
        initSentryForDebugging();
    }
    if (mode === "studio") {
        window.__SOFTR_IS_IN_STUDIO = true;
    }
    if (studioUser) {
        log.info("Studio passed a logged in user to the page renderer", { studioUser });
        window.logged_in_user = studioUser;
    }
    const spaMode =
        "spa_mode_enabled" in context.application_context
            ? (context.application_context.spa_mode_enabled ?? false)
            : SPA_MODE_DEFAULT_VAULE;

    const featureFlags = { ...FEATURE_FLAG_DEFAULTS, ...providedFeatureFlags };

    if (providedFeatureFlags?.universalDSForAirtable) {
        window.universalDSForAirtable = true;
    }
    Sentry.setTag("softr.spa_mode", spaMode);
    Sentry.setTag(
        "softr.has_vibe_coding_block",
        blocks.some((b) => b.name === "vibe-coding"),
    );

    if (spaMode) {
        removeViewTransitions();
    }
    initTrafficFeatureFlags(appId);

    if (shouldRegisterServiceWorker(context.application_context)) {
        interceptAndStoreBeforeInstallPromptEvent();
    }

    let pageRef: PageRef | null = null;
    function setPageRef(ref: PageRef) {
        pageRef = ref;
    }

    const lifecycle = new Lifecycle();
    lifecycle.push(() => (contentRoot.innerHTML = ""));
    const enableScripts = mode !== "studio";
    contentRoot.setAttribute("data-spr-role", "content-root");

    const reactRoot = createRoot(contentRoot, {
        // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
        onRecoverableError: Sentry.reactErrorHandler(),
    });
    lifecycle.push(() => reactRoot.unmount());
    const blockStabilizer = makeBlockStabilizer({ definitions: blocks, theme });

    const appConstants: AppConstants = {
        appId,
        baseUrl,
        blockIdAttributeName,
        slotBlockIdAttributeName,
        slotIdAttributeName,
        mode,
        spaMode,
        featureFlags,
        lifecycle,
        studioEventEmitter,
        aiApp,
        badgeType,
        showMadeWithBadge,
    };

    const addBlockAPIReadyListener: PageAPI["addBlockAPIReadyListener"] = (callback) => {
        const f = (event: BlockAPIReadyEvent) => {
            callback({
                api: event.detail.api ?? null,
                blockName: event.detail.blockName,
                blockVersion: event.detail.blockVersion,
            });
        };
        const cleanup = BlockAPIReadyEvent.listen(f);

        // Studio typically attaches the listener when the settings panel is opened,
        // which means the block API is likely already fetched and without the following loop,
        // the studio will be stuck forever wating for an event that never happens.
        // So we invoke the listener immediately with the block APIs that have already been fetched
        for (const [blockKey, api] of blockAPIs.entries()) {
            const [blockName, blockVersion] = blockKey.split(":::");
            callback({ api, blockName, blockVersion });
        }

        // Just in case the caller forgets to call the returned cleanup function,
        // we can also clean it up when the page renderer is disposed (the studio calls
        // dispose on page transitions).
        lifecycle.push(cleanup);

        return cleanup;
    };

    await withPatchedLoadListener(lifecycle, () => firstRender(reactRoot));

    // We return after the "block roots" are ready. The studio relies on this
    // to draw overlays on top of the blocks.
    return {
        update,
        getBlockById,
        compileVibeCodingBlock,
        dispose: () => {
            log.info("Disposing the page renderer");
            reactRoot.unmount();
            lifecycle.dispose();
        },
        addBlockAPIReadyListener,
    };

    // at the moment you can call this function before the block had the chance to render and before it had the chance to set it's ref
    // this is left unsolved until we start integrating the studio canvas part
    // but either way this function needs to stay sync
    function getBlockById(id: string): BlockInstance | null {
        if (!pageRef) {
            throw new Error("Page ref is not ready.");
        }

        const blockInstance = pageRef.blockInstanceMap.get(id);
        if (!blockInstance) return null;
        return blockInstance;
    }

    async function firstRender(reactRoot: Root) {
        insertHeadStylesSentinel();
        const { definitions: blocks, theme } = blockStabilizer.currentState();
        contentRoot.classList.add(CONTENT_ROOT_CLASS);
        lifecycle.push(() => contentRoot.classList.remove(CONTENT_ROOT_CLASS));
        installStylesheet(lifecycle, { theme, mode, featureFlags });
        if (!spaMode) {
            // There's real reason to use this API in the non-SPA mode,
            // however, it's still nice to be able to expose these APIs
            // so that SPA compatible custom code can run in the non-spa mode
            // without issues.
            // Makes toggling the SPA mode on/off surprise free.
            window.SOFTR_PAGE = softrPageInNonSPAApps(lifecycle);
        }
        if (appCustomHeaderCode && enableScripts) {
            await injectCustomCode({
                at: document.head,
                code: appCustomHeaderCode,
                debugLabel: "appHeader",
                lifecycle,
            });
        }

        // Page custom code is executed by spa.tsx in the SPA mode
        // because it needs to run on page transitions
        if (pageCustomHeaderCode && enableScripts && !spaMode) {
            await injectCustomCode({
                at: document.head,
                code: pageCustomHeaderCode,
                debugLabel: "pageHeader",
                lifecycle,
            });
        }

        setDataAttributes(contentRoot, { appId, pageId, workspaceId, paymentPlan });

        const page = (
            <StrictMode>
                <AppConstantsContext.Provider value={appConstants}>
                    <AppRoot
                        ref={setPageRef}
                        definitions={blocks}
                        theme={theme}
                        pageId={pageId}
                        context={context}
                        appHeaderCustomCode={appCustomHeaderCode ?? ""}
                        pageHeaderCustomCode={pageCustomHeaderCode ?? ""}
                        pageFooterCustomCode={pageCustomFooterCode ?? ""}
                    />
                </AppConstantsContext.Provider>
            </StrictMode>
        );
        // Start listening for block loading before the rendering
        const waitForLoadedBlocksPromise = Promise.allSettled([
            // v3 custom code block emits a custom event after its scripts are executed
            // which means that their "DOMContentLoaded" listeners have been attached
            waitForLegacyCustomCodeBlocksWithTimeout(blocks, 5000),
            // Custom code blocks' DOMContentLoaded listeners expect react block roots
            // to be present in the DOM so we wait for them here
            waitForReactBlocksWithTimeout(lifecycle, blocks, 5000),
        ]);
        // When script execution is enabled, we need to make sure
        // that the react tree is fully rendered before we dispatch
        // the custom event, to make sure that the scripts can
        // se the blocks.
        // Otherwise, inside studio, where the scripts are disabled,
        // it's better to not do this synchronously so that the page
        // can stay responsive.
        if (enableScripts) {
            flushSync(() => reactRoot.render(page));
        } else {
            startTransition(() => {
                reactRoot.render(page);
            });
        }

        if (appCustomFooterCode && enableScripts) {
            await injectCustomCode({
                at: contentRoot,
                code: appCustomFooterCode,
                debugLabel: "appFooter",
                lifecycle: lifecycle,
            });
        }

        // Page custom code is executed by spa.tsx in the SPA mode
        // because it needs to run on page transitions
        if (pageCustomFooterCode && enableScripts && !spaMode) {
            await injectCustomCode({
                at: contentRoot,
                code: pageCustomFooterCode,
                debugLabel: "pageFooter",
                lifecycle: lifecycle,
            });
        }
        waitForReactBlocks(lifecycle, blocks, new AbortController().signal).then(() => {
            window.dispatchEvent(new CustomEvent("spr-all-react-blocks-ready"));
        });

        if (enableScripts) {
            await waitForLoadedBlocksPromise;

            // Technically, it valid to dispatch the event before `waitForReactBlocksWithTimeout`
            // call, and this is similar to what Firefox does. However, there are some existing
            // apps out there that assume that DOMContentLoaded is fired after the client side
            // blocks are rendered. I'm not sure what exact logic that Chrome uses to decide when
            // DOMContentLoaded should be fired, but it seems to be after some of the react blocks
            // are rendered.
            //
            // We're doing the same thing maintain compatibility with those apps, even if their code is
            // wrong.
            document.dispatchEvent(new CustomEvent(SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED, { bubbles: true }));
            // "load" event doesn't bubble and is non cancelable
            // https://developer.mozilla.org/en-US/docs/Web/API/Window/load_event
            document.dispatchEvent(new CustomEvent(SOFTR_PAGE_RENDERER_LOAD, { bubbles: false, cancelable: false }));
            window.dispatchEvent(new CustomEvent(SOFTR_PAGE_RENDERER_LOAD, { bubbles: false, cancelable: false }));
        }
        await waitForBlockRoots(blocks);
    }

    /**
     * If converting this function to async, test the studio interactions **very**
     * carefully. There might be some race conditions that might prop up
     * and we'll have probably use some locking mechanism to prevent them.
     * Mainly so that we don't delete the block roots while the react tree
     * is rendering.
     */
    async function update({ blocks, theme, pageId, appId, context }: PageRendererUpdateArgs): Promise<void> {
        if (isHeavyLoggingEnabled()) {
            log.debug("SoftrPageRenderer.update", { blocks, theme, pageId, appId });
        }
        const { definitions: newBlocks, theme: newTheme } = blockStabilizer.update({ definitions: blocks, theme });

        setDataAttributes(contentRoot, { appId, pageId, workspaceId, paymentPlan });

        // Changing block settings can potentially cause a lot of work,
        // especially in heavy blocks like Table.
        // Wrapping it in startTransition helps keep the page responsive
        // while the react tree is being updated.
        // To see the difference, try removing startTransition, add 3-4
        // table blocks on a page. Then move the column width slider in
        // the style settings.
        startTransition(() => {
            updateStylesheet({ theme: newTheme, mode, featureFlags });
            reactRoot.render(
                <StrictMode>
                    <AppConstantsContext.Provider value={appConstants}>
                        <AppRoot
                            ref={setPageRef}
                            definitions={newBlocks}
                            theme={newTheme}
                            pageId={pageId}
                            context={context}
                            // Empty because update is only called from the studio, where we don't execute
                            // custom code
                            appHeaderCustomCode=""
                            pageHeaderCustomCode=""
                            pageFooterCustomCode=""
                        />
                    </AppConstantsContext.Provider>
                </StrictMode>,
            );
        });

        await waitForBlockRoots(newBlocks);
    }

    // This is a temporary method for studio-ui to build the vibe coding block until we move it to the BE
    async function compileVibeCodingBlock({
        source,
        dataSourceCount,
        signal,
    }: CompileVibeCodingBlockProps): Promise<CompileVibeCodingBlockResult> {
        log.debug("SoftrPageRenderer.compileVibeCodingBlock", { source });
        return vibeCompiler.compileVibeBlock({
            blockFile: source,
            mode: "production",
            dataSourceCount,
            // can specify additional components to be included in the bundle
            // LLM should eventually be able to do a tool call to add components
            // to a project as needed
            //
            // e.g. ["https://ui.aceternity.com/registry/carousel.json"]
            components: [],
            signal,
        });
    }
}

function setDataAttributes(
    element: HTMLElement,
    {
        appId,
        pageId,
        workspaceId,
        paymentPlan,
    }: Pick<PageRendererArgs, "appId" | "pageId" | "workspaceId" | "paymentPlan">,
) {
    element.setAttribute("data-appid", appId);
    element.setAttribute("data-pageid", pageId);
    element.setAttribute("data-workspaceid", workspaceId);
    element.setAttribute("data-paymentplan", paymentPlan);
}

type GeneratePageRendererCSSProps = Pick<PageRendererArgs, "theme" | "mode"> & {
    featureFlags: PageRendererFeatureFlags;
};

function generatePageRendererCSS({ theme, mode, featureFlags }: GeneratePageRendererCSSProps) {
    let styleText = `
        .${CONTENT_ROOT_CLASS} {
            background-color: ${theme.defaultBackgroundColor};
        }

        .${CONTENT_ROOT_CLASS} .spr-hidden {
            display: none;
        }
    `;
    if (mode !== "studio" && !embeddedMode && !featureFlags.navigationLayout) {
        // This bit of CSS ensures that the footer sticks to the bottom of the page
        // when the content is smaller than the viewport.
        // We can't enable this inside the studio/embedded mode because we
        // render the page inside an iframe whose height resizes
        // based on its contents and we don't want an infinitely
        // expanding iframe.
        styleText += `
            .${CONTENT_ROOT_CLASS} {
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                min-height: 100dvh;
            }

            /**
             * Blocks have a data-block="blockname-vx-y-z" attribute,
             * so the following will select all the footer blocks.
             */
            .${CONTENT_ROOT_CLASS} > [data-block^="footer"] {
                margin-top: auto;
            }

            .${CONTENT_ROOT_CLASS} > .dynamic-height {
                flex: 1;
                display: flex;
                flex-direction: column;
            }

            /**
             * Hack for v2 header block
             *
             * It always has #vertical-holder and #data-content-vertical that
             * should be displayed in a flex row
             */
            .${CONTENT_ROOT_CLASS}:has(#vertical-holder) {
                flex-direction: row;
            }

            /**
             * Hack for fixed header + scroll to block:
             * Fixed header should set --sticky-nav-height css variable, so scroll to block position correctly
             */
            .${CONTENT_ROOT_CLASS} [data-block] {
                scroll-margin-top: var(--sticky-nav-height, 0px);
            }
        `;
    }

    if (mode === "studio") {
        styleText += STUDIO_CSS_HACKS;
        styleText += STUDIO_STYLES;
    }

    if (featureFlags.navigationLayout) {
        styleText += `
            .${CONTENT_ROOT_CLASS} {
                display: grid;
                grid-template-areas: "topbar    topbar"
                                     "sidebar   main"
                                     "bottombar bottombar";
                grid-template-columns: auto minmax(0, 1fr);
            }

            #topbar-root {
                grid-area: topbar;
            }

            /**
             * When topbar does not exist, make sure page does not get clipped when opened from homescreen.
             */
            #topbar-root:empty::before {
                content: '';
                display: block;
                padding-top: env(safe-area-inset-top);
            }

            #sidebar-root {
                grid-area: sidebar;
            }

            #bottombar-root {
                grid-area: bottombar;
            }

            #main-content {
                grid-area: main;
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                min-height: 100dvh;
            }

            /**
             * Tab container buttons in the Studio UI has a special z-index values to be accessible.
             * However, we don't want them to be on top of the elements outsize the main-content (e.g. sticky header).
             */
            #main-content[data-instudio=true] {
                z-index: 1;
            }

            /**
             * Blocks have a data-block="blockname-vx-y-z" attribute,
             * so the following will select all the footer blocks.
             */
            #main-content > [data-block^="footer"] {
                margin-top: auto;
            }

            #main-content > .dynamic-height {
                flex: 1;
                display: flex;
                flex-direction: column;
            }

            /**
             * Hack for sticky top nav + scroll to block:
             * Sticky top nav should set --sticky-nav-height css variable, so scroll to block position correctly
             */
            #main-content [data-block] {
                scroll-margin-top: var(--sticky-nav-height, 0px);
            }

            /**
             * Having any different overflow value would lead to elements
             * with "position: sticky" becoming hidden when page is scrolled
             * and a modal is open. This does undo a hack for v4 and older blocks.
             * If you do find an element misbehaving, rather fix it where the problem
             * occurs instead of here.
             */
            html {
                overflow: visible;
            }
        `;
        if (mode === "studio") {
            styleText += `
                /**
                 * Make sure each block is selectable in the studio, even if it has
                 * no content.
                 * We skip this for the dynamic-height blocks because they have a 100%
                 * height, and applying min-height to it changes the meaning of 100%.
                 */
                #main-content [data-block]:not(.dynamic-height) {
                    min-height: 90px;
                }
            `;
        }
    }

    // Some old blocks add a min-height to the body tag.
    // This is unexpected for embedded mode, since it affects the overall page height.
    if (embeddedMode) {
        styleText += `
            body, #main-content {
                min-height: initial;
            }
        `;
    }

    // Disable overscroll
    // `overscroll-behavior: none;` doesn't work properly in Safari (try scrolling while the cursor is on the chart)
    // and embed mode (it disables the scroll completely there)
    if (!embeddedMode && !isSafari) {
        // Scoped to pointer devices only — touch devices need overscroll for native pull-to-refresh
        styleText += `
            @media (hover: hover) and (pointer: fine) {
                html, body {
                    overscroll-behavior: none;
                }
            }
        `;
    }

    return styleText;
}

const STYLESHEET_ID = "softr-page-renderer-styles";

function installStylesheet(lifecycle: Lifecycle, props: GeneratePageRendererCSSProps) {
    const commentNode = document.createComment(
        "Stick footer to the bottom when the page content is smaller than the viewport",
    );
    document.head.appendChild(commentNode);
    lifecycle.push(() => commentNode.remove());
    lifecycle.push(() => styles.remove());

    const styles = document.createElement("style");
    styles.setAttribute("id", STYLESHEET_ID);
    styles.innerHTML = generatePageRendererCSS(props);
    document.head.appendChild(styles);
}

function updateStylesheet(props: GeneratePageRendererCSSProps) {
    const styles = document.getElementById(STYLESHEET_ID);

    if (styles) {
        styles.innerHTML = generatePageRendererCSS(props);
    }
}

const CONTENT_ROOT_CLASS = "spr-content-root";
const STUDIO_CSS_HACKS = `
    /*
     * The divider field in v4 blocks has a 100% height within a flex container
     * causing it to expand in a weird way.
     * Try removing this rule and add a v4 list block ("List with horizontal cards")
     * to replicate the issue.
     *
     * The issue will be fixed on the blocks side as well, but that won't fix
     * the older versions of the blocks that are already published.
     * This is why, this hack is necessary.
     */
    .${CONTENT_ROOT_CLASS} div[data-field-type="divider"] > div.list-field-element {
        height: unset !important;
    }

    /**
     * Disable sticky header for v2/v1 header blocks
     */
    .${CONTENT_ROOT_CLASS} nav[data-block-id] {
        position: relative !important;
    }

    /**
     * Disable sticky header for v1.0.0 header.
     *
     * "[data-block-id]" does not exist for this version.
     */
    .${CONTENT_ROOT_CLASS} nav[data-studio-block-id] {
        position: relative !important;
    }

    /**
     * Hack for v3 gallery blocks with an auto sliding gallery
     * The selector below selects .slick-slider elements inside
     * an element with a data-block attribute that starts with "gallery"
     * and contains "v3" in it.
     */
    .${CONTENT_ROOT_CLASS} [data-block^="gallery"][data-block*="v3"] .slick-slider
    {
        height: max-content;
    }

    /**
     * Hack for v1 and v2 Simple text block having extra space at the bottom
     */
    .${CONTENT_ROOT_CLASS} [data-block^="text1"] .ql-editor {
        height: unset !important;
    }

    /**
     * Hack for v1 testimonial block
     */
    .${CONTENT_ROOT_CLASS} .swiper-wrapper {
        height: unset !important;
    }

    .${CONTENT_ROOT_CLASS} [data-block^="video2-v3"] {
        max-height: 100vh;
    }

    .${CONTENT_ROOT_CLASS} [data-block^="image2-v3"] {
        max-height: 100vh;
    }

    .${CONTENT_ROOT_CLASS} [data-block^="image2-v3"] img.sw-height-full {
        height: initial !important;
    }

    /**
     * Prevent the page from becoming horizontally scrollable when
     * there are elements that try to take up the full width of the page.
     * To replicate the issue, add a hero block (any version with a tile
     * section would do).
     * Add a word long enough to overflow the screen width e.g. aaaaa....aaaaa
     * and you'll see that the page becomes horizontally scrollable.
     */
    html {
        overflow-x: hidden;
    }
`;
const STUDIO_STYLES = `
    /**
     * Make sure each block is selectable in the studio, even if it has
     * no content.
     */
    .${CONTENT_ROOT_CLASS} > [data-block] {
        min-height: 90px;
    }

    .${CONTENT_ROOT_CLASS} > .spr-navigation-placeholder {
        display: none;
    }

    /**
     * Headers are typically smaller than 90px so we don't want to
     * force them to expand.
     */
    .${CONTENT_ROOT_CLASS} > [data-block^="header"] {
        min-height: initial;
    }
`;

const BlockContainerSlotContext = createContext<BlockContainerSlotContextValue | null>(null);

export const SoftrPageRenderer: PageRenderer = {
    render,
    ChildBlock,
    BlockContainerSlotContext,
    AppContext: AppContext,
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    Link: SPALink,
    useSPALinkClick,
    spaNavigate,
    addNavigationListener,
    withTaggedScope: (tags, callback) => {
        return Sentry.withScope((scope) => {
            Object.entries(tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
            return callback();
        });
    },
    monitoring: {
        captureException: (exception) => {
            return Sentry.captureException(exception);
        },
        captureMessage: (message, severityLevel) => {
            return Sentry.captureMessage(message, severityLevel);
        },
    },
    setOpenPageModal,
    setModalSearchParams,
    useOpenPageModal,
    useModalRecord,
    useRecordsByBlock,
    useModalDepth,
    useNavigationBlocker,
    usePushPrompt,
};
export function lastNonFooterBlockIn(blocks: readonly BlockDefinition[]): BlockDefinition | undefined {
    return [...blocks].reverse().find((block) => block.category.toLowerCase() !== "footer");
}

async function waitForReactBlocksWithTimeout(
    lifecycle: Lifecycle,
    blocks: readonly BlockDefinition[],
    timeout: number,
) {
    const abortController = new AbortController();
    await Promise.race([
        waitForReactBlocks(lifecycle, blocks, abortController.signal),
        // Timeout after 5 seconds to prevent any blocks that are stuck
        // from blocking the scripts from executing.
        sleep(timeout).then(() => abortController.abort()),
    ]);
}
/**
 * V4 custom code blocks top level scripts must be executed before we emit the synthetic
 * "DOMContentLoaded" and "load" events, otherwise the user's code might never be called
 */
async function waitForLegacyCustomCodeBlocksWithTimeout(blocks: readonly BlockDefinition[], timeout: number) {
    const abortController = new AbortController();
    await Promise.race([
        waitForLegacyCustomCodeBlocks(blocks, abortController.signal),
        // Timeout after 5 seconds to prevent any blocks that are stuck
        // from blocking the scripts from executing.
        sleep(timeout).then(() => abortController.abort()),
    ]);
}

async function waitForLegacyCustomCodeBlocks(blocks: readonly BlockDefinition[], signal: AbortSignal) {
    const customCodeBlocks = blocks.filter((block) => isPreCompiledBlock(block) && isCustomCodeBlock(block));
    const loadedSet = new Set<string>();
    // window.dispatch(new CustomEvent("softr/legacy-custom-code-scripts-executed", { detail: { id: definition.id } }))
    // in PreComiledBlock.tsx

    const listener = (event: Event) => {
        if (signal.aborted) {
            return;
        }
        const customEvent = event as CustomEvent<{ id: string }>;
        loadedSet.add(customEvent.detail.id);
    };
    window.addEventListener("softr/legacy-custom-code-scripts-executed", listener, { signal });
    while (!customCodeBlocks.every((block) => loadedSet.has(block.id))) {
        await sleep(10);
    }
}

/**
 * Each react block emits a custom event once its component script is fetched
 * and the block is rendered.
 * This function waits for all the react blocks to emit this event,
 * then it calls flushSync to ensure that the last block's DOM has been rendered
 */
function waitForReactBlocks(lifecycle: Lifecycle, blocks: readonly BlockDefinition[], signal: AbortSignal) {
    const dynamicBlocks = blocks.filter((block) => !isPreCompiledBlock(block));
    const loadedSet = new Set<string>();
    if (dynamicBlocks.every((block) => loadedSet.has(block.id))) {
        return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
        const { dispose } = addReactBlockReadyEventListener((event) => {
            if (signal.aborted) {
                return reject("ABORTED");
            }
            loadedSet.add(event.detail.blockId);
            if (dynamicBlocks.every((block) => loadedSet.has(block.id))) {
                resolve();
                // Once the last block has been loaded,
                // no need to keep the listener around. We can dispose it.
                dispose();
            }
        });
        // Cleanup the listener if the page renderer itself is disposing
        lifecycle.push(() => dispose());
        // Cleanup the listener if aborted (e.g. due to timeout)
        signal.addEventListener("abort", () => dispose());
    });
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

const FEATURE_FLAG_DEFAULTS: PageRendererFeatureFlags = {
    navigationLayout: false,
};

function installViewTransitions(): () => void {
    const rootStyle = document.createElement("style");
    rootStyle.innerHTML = `
      @view-transition {
        navigation: auto;
      }

      @keyframes fade-out-with-delay {
        0% {
          opacity: 1;
        }
        /* delay the start of the animation
         * If we start fading out immediately, the new sidebar/topbar will not be
         * visible when we start fading out leading to
         */
        80% {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }

      @keyframes fade-in-with-delay {
        0% {
          opacity: 0;
        }
        10% {
          opacity: 1;
        }
        to {
          opacity: 1;
        }
      }


      @keyframes fade-in {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }

      @keyframes fade-out {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }

      @keyframes fade-out-immediately {
        from {
          opacity: 0;
        }
        to {
          opacity: 0;
        }
      }

      #sidebar-root {
        view-transition-name: sidebar;
      }
      ${viewTransitionGroup("sidebar", { newAnimation: "fade-in-with-delay", oldAnimation: "fade-out-with-delay", duration: NAV_TRANSITION_DURATION })}

      ::view-transition-group(sidebar) {
          /* Chrome adds a slide-in-out animation by default which looks really bad
           * because it makes the sidebar fly out of the frame in a weird way.
           **/
          animation-name: none;
      }

      ::view-transition-old(sidebar) {
          width: unset;
      }

      #topbar-root {
        view-transition-name: topbar;
      }
      ${viewTransitionGroup("topbar", { newAnimation: "fade-in", oldAnimation: "fade-out-with-delay", duration: NAV_TRANSITION_DURATION })}


    `;
    function viewTransitionGroup(
        name: string,
        opts: {
            duration?: string;
            newAnimation?: string;
            oldAnimation?: string;
        },
    ) {
        return `
          ::view-transition-group(${name}) {
            animation-duration: ${opts.duration};
          }
          ::view-transition-new(${name}) {
            animation-name: ${opts.newAnimation};
          }
          ::view-transition-old(${name}) {
            animation-name: ${opts.oldAnimation};
          }
        `;
    }

    document.head.appendChild(rootStyle);
    const clickListener = (e: MouseEvent) => {
        // Immediately add a data-active attribute to the clicked link so that it can be highlighted before the
        // navigation has completed.

        const sidebarRoot = document.getElementById("sidebar-root");
        const topbarRoot = document.getElementById("topbar-root");
        if (!(e.target instanceof HTMLElement)) {
            return;
        }
        if (!sidebarRoot?.contains(e.target) && !topbarRoot?.contains(e.target)) {
            return;
        }

        // The topbar links might contain a single span within them.
        // Depending on where exactly you click, the onClick event might be triggered on the inner span
        // instead of the parent `<a>` tag, so we try to unwrap it here.
        // To see this in action, try removing this logic and clicking on the topbar link text vs the border outside the
        // text. Clicking the text will not activate the link.
        //
        // I also tried to rely on the event bubbling behaviour, but it seems like when you click on the span,
        // the event is not bubbled up to the parent `<a>` element.
        // This bit of code is kind of coupled to the implementation details of topbar links, but I couldn't figure out
        // how to do this any other way.
        let targetAnchor: HTMLAnchorElement;
        if (e.target instanceof HTMLAnchorElement) {
            targetAnchor = e.target;
        } else if (e.target instanceof HTMLElement && e.target.parentElement instanceof HTMLAnchorElement) {
            targetAnchor = e.target.parentElement;
        } else {
            return;
        }

        const sidebarLinks = Array.from(sidebarRoot?.querySelectorAll("a") ?? []);
        const topbarLinks = Array.from(document.getElementById("topbar-root")?.querySelectorAll("a") ?? []);
        const targetHref = targetAnchor.href;
        for (const anchorChild of [...sidebarLinks, ...topbarLinks]) {
            // Keep data-active on links that point to the same destination as the clicked link
            if (anchorChild === targetAnchor || urlMatchesDestination(targetHref, anchorChild.href)) {
                anchorChild.dataset.active = "true";
            } else {
                delete anchorChild.dataset.active;
            }
        }
    };
    window.addEventListener("click", clickListener);

    // Disable the navigation transitions when the user signs out.
    // This is because the sidebar/topbar will most likely be removed
    // on signout. It looks a bit odd.
    const signOutListener = () => {
        rootStyle.remove();
    };
    window.addEventListener("user-sign-out", signOutListener);
    return () => {
        window.removeEventListener("click", clickListener);
        window.removeEventListener("user-sign-out", signOutListener);
        rootStyle.remove();
    };
}

function shouldEnableSPA() {
    try {
        return window.location.search.toLowerCase().replaceAll(/[-_]/g, "").includes("experimentalspaenable");
    } catch {
        return false;
    }
}
function shouldDisableSPA() {
    try {
        return window.location.search.toLowerCase().replaceAll(/[-_]/g, "").includes("experimentalspadisable");
    } catch {
        return false;
    }
}
function softrPageInNonSPAApps(lifecycle: Lifecycle): typeof window.SOFTR_PAGE {
    const pageAbortController = new AbortController();
    lifecycle.push(() => pageAbortController.abort());
    async function waitFor(condition: () => unknown) {
        if (typeof condition !== "function") {
            throwAtCaller(
                waitFor,
                new Error("waitFor must be called with a function argument. Did you forget a '() => '?"),
            );
        }
        while (!condition()) {
            await timeout(50, { signal: pageAbortController.signal });
        }
    }
    return {
        beforeUnload(cb) {
            lifecycle.push(cb);
        },
        waitFor,
    };
}
async function waitForBlockRoots(blocks: readonly BlockDefinition[]): Promise<void> {
    const parentIdMap = getBlockParentIdMap(blocks);
    let timeoutElapsed = false;
    setTimeout(() => (timeoutElapsed = true), 100);
    const topLevelBlocks = blocks.filter((block) => !parentIdMap[block.id]);
    while (
        !timeoutElapsed &&
        !topLevelBlocks.every((block) => document.querySelector(`[data-spr-block-id="${block.id}"]`))
    ) {
        await new Promise((resolve) => setTimeout(resolve, 10));
    }
}

// Keep track of all the block APIs that have been fetched already so that when
// the studio attaches a onBlockAPIReady listener, we can immediately call it for blocks
// that have already been fetched. Because of caching, the event only arrives once per block name/version.
//
// This is global because the page renderer might be unmounted and remounted by the studio (typically when
// a user switches between pages). The event still happens only once, so if the page renderer is remounted,
// we won't receive the event again.
const blockAPIs = new Map<
    // blockName:::blockVersion
    `${string}:::${string}`,
    BlockAPI | null
>();
BlockAPIReadyEvent.listen((event) => {
    const { blockName, blockVersion, api } = event.detail;
    blockAPIs.set(`${blockName}:::${blockVersion}`, api ?? null);
});
